import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Input,
  Flex,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItemOption,
  MenuOptionGroup,
  Text,
  Checkbox,
  VStack,
} from "@chakra-ui/react";
import ValidatedInput from "components/addForm/ValidatedInput";
import SelectInput from "components/addForm/SelectInput";
import useApi from "hooks/useApi";
import { FEATURES_LISTING_API } from "constants/endpoints";

const LIMIT_UNITS = {
  daily: "DAILY",
  weekly: "WEEKLY",
  monthly: "MONTHLY",
  plan: "PLAN",
  unlimited: "UNLIMITED",
};

const FeaturesComponent = ({ formData = {}, onChange, error }) => {
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { apiCall } = useApi();
  const PAGE_LIMIT = 2000;
  const menuRef = useRef();

  const fetchData = useCallback(async (query = "") => {
    const reorderFeaturesByPriority = (priority, all) => {
      const priorityKeys = new Set(priority.map(p => p.key));
      const selected = [];
      const notSelected = [];
    
      for (const item of all) {
        if (priorityKeys.has(item.key)) {
          selected.push(item);
        } else {
          notSelected.push(item);
        }
      }
    
      return [...selected, ...notSelected];
    };
    
    try {
      const response = await apiCall.get(
        `${FEATURES_LISTING_API}?limit=${PAGE_LIMIT}&search_text=${query}`
      );
      const rawData = response?.data?.features || [];
      const formattedData=reorderFeaturesByPriority(selectedFeatures, rawData);
      setFilteredFeatures(formattedData);
    } catch (error) {
      console.error("Error fetching features listing:", error);
    }
  }, [selectedFeatures]);

  useEffect(() => { 
    fetchData();
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedFeatures]);
  
  useEffect(() => {
    if (formData.features) {
      const featuresArray = Array.isArray(formData.features)
        ? formData.features
        : [formData.features];
      setSelectedFeatures(featuresArray);
    }
  }, [formData.features]);

  const handleFeatureChange = (key, e) => {
    const { name, value } = e.target;
    const updatedFeatures = selectedFeatures.map((feature) =>
      feature.key === key ? { ...feature, [name]: value } : feature
    );
    setSelectedFeatures(updatedFeatures);
    onChange({
      target: {
        name: "features",
        value: updatedFeatures, 
      },
    });
  };

  const removeFeature = (key) => {
    const updatedFeatures = selectedFeatures.filter(
      (feature) => feature.key !== key
    );
    setSelectedFeatures(updatedFeatures);
    onChange({
      target: {
        name: "features",
        value: updatedFeatures,
      },
    });
  };

  return (
    <Box mb={6}>
      <Heading as="h3" size="md" mb={4}>
        Features
      </Heading>
      <Box mb={4} position="relative" ref={menuRef}>
        <Input
          placeholder="Search for a feature..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsMenuOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value?.trim()) {
              fetchData(e.target.value?.trim());
            }
          }}
        />

        {isMenuOpen && (
          <Box
            position="absolute"
            width="100%"
            zIndex={1000}
            maxHeight="300px"
            overflowY="auto"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            mt={2}
            p={2}
          >
            <VStack align="stretch" spacing={2}>
              {filteredFeatures.map((feature) => {
                const isChecked = selectedFeatures.some(
                  (item) => item.key === feature.key
                );
                return (
                  <Checkbox
                    key={feature.key}
                    isChecked={isChecked}
                    onChange={(e) => {
                      let updatedFeatures;
                      if (e.target.checked) {
                        updatedFeatures = [
                          ...selectedFeatures,
                          {
                            key: feature.key,
                            value: "",
                            unit: "plan",
                            index: selectedFeatures?.length,
                            value_template: feature?.value_template,
                          },
                        ];
                      } else {
                        updatedFeatures = selectedFeatures.filter(
                          (item) => item.key !== feature.key
                        );
                      }
                      setSelectedFeatures(updatedFeatures);
                      onChange({
                        target: {
                          name: "features",
                          value: updatedFeatures,
                        },
                      });
                    }}
                  >
                    <Text w="100%">
                      {feature.value_template} 🔹 {feature.label} 🔹{" "}
                      {feature.key}
                    </Text>
                  </Checkbox>
                );
              })}
            </VStack>
          </Box>
        )}
      </Box>
      {selectedFeatures.map((feature) => (
        <Flex direction={"column"} gap={4}>
          <Text fontSize={18}>
            {feature?.value_template?.replace(
              "{{value}}",
              feature?.value === "" ? "{{value}}" : feature?.value
            )}
          </Text>
          <Flex direction={{base:'column', md:'row'}} key={feature.key} gap={{base:0, md:3}} mb={4} alignItems={{ base:'start', md:"center"}} w={{base:'full', md:'auto'}} pl={{base:'20px', md:'auto'}} >
            <Flex flex={1} w={{base:'full', md:'auto'}}>
              <ValidatedInput
                name="key"
                label="Key"
                placeholder="Enter Key"
                type="text"
                value={feature.key || ""}
                isDisabled={true}
                onChange={(e) => handleFeatureChange(feature.key, e)}
                error={error?.key}
              />
            </Flex>
            <Flex flex={1} w={{base:'full', md:'auto'}}>
              <ValidatedInput
                name="value"
                label="Value"
                type="text"
                placeholder="Enter Value"
                value={feature.value || ""}
                onChange={(e) => handleFeatureChange(feature.key, e)}
                error={error?.value}
              />
            </Flex>
            <Flex flex={1} w={{base:'full', md:'auto'}}>
              <ValidatedInput
                name="daily_limit"
                label="Daily Limit"
                type="text"
                placeholder="Enter Value"
                value={feature.daily_limit || ""}
                onChange={(e) => handleFeatureChange(feature.key, e)}
                error={error?.value}
              />
            </Flex>
            <Flex flex={1} w={{base:'full', md:'auto'}}>
              <ValidatedInput
                name="index"
                label="Index"
                type="number"
                placeholder="Enter position"
                value={feature.index || ""}
                onChange={(e) => handleFeatureChange(feature.key, e)}
                error={error?.index}
              />
            </Flex>
            <Flex flex={1} w={{base:'full', md:'auto'}}>
              <SelectInput
                name="unit"
                label="Unit"
                options={LIMIT_UNITS}
                selectedValue={feature.unit || ""}
                onChange={(e) => handleFeatureChange(feature.key, e)}
                error={error?.unit}
              />
            </Flex>
            <Button
              onClick={() => removeFeature(feature.key)}
              colorScheme="red"
              size="sm"
            >
              Remove
            </Button>
          </Flex>
        </Flex>
      ))}
    </Box>
  );
};

export default FeaturesComponent;
